import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import {
  getPassportFlags,
  isPassportEnabled,
  listPassportCollections,
  type PassportCollectionDefinition,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE } from './passportCopy';
import { PassportChrome } from './PassportChrome';

const PassportCollectionsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [collections, setCollections] = useState<PassportCollectionDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/passport/collections' }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPassportCollections();
      setCollections(data);
      void apiService.trackEvent('passport_collection_viewed', { metadata: { view: 'list' } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags) || !flags?.passport_collections_enabled) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  if (authLoading || !isAuthenticated || flagsLoading) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags) || !flags?.passport_collections_enabled) {
    return (
      <PassportChrome active="collections" flags={flags}>
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PassportChrome>
    );
  }

  return (
    <PassportChrome
      active="collections"
      flags={flags}
      title={
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            Official encounter sets
          </h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Structured sets built from your verified encounters: manufacturer, chassis, event, and
            more.
          </p>
        </div>
      }
    >
      {loading ? (
        <p className="text-zinc-500 text-sm" role="status">
          Loading collections…
        </p>
      ) : error ? (
        <EmptyState
          title="Couldn't load collections"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : collections.length === 0 ? (
        <EmptyState title="No collections yet" description="Check back as more collections are published." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((c) => {
            const pct = Math.round(Math.min(100, Math.max(0, c.progress?.percent_complete ?? 0)));
            const done = c.progress?.status === 'completed' || pct >= 100;
            return (
              <div
                key={c.public_id}
                className={`rounded-2xl border p-5 ${
                  done
                    ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] to-[#0c0c0c]'
                    : 'border-white/[0.08] bg-[#0c0c0c]'
                }`}
              >
                {done ? (
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/90 mb-1">
                    Complete
                  </p>
                ) : (
                  <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                    {c.collection_type.replace(/_/g, ' ')}
                  </p>
                )}
                <h2 className="text-base font-semibold text-white mb-1"><Link className="underline underline-offset-4" to={`/passport/collections?set=${encodeURIComponent(c.slug)}`}>{c.title}</Link></h2>
                {c.subtitle ? <p className="text-sm text-zinc-400 mb-3">{c.subtitle}</p> : null}
                {c.progress ? (
                  <div className="mt-3">
                    <div
                      className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden"
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${c.title} ${pct}% complete`}
                    >
                      <div
                        className={`h-full rounded-full ${done ? 'bg-amber-400/80' : 'bg-zinc-300/70'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5">
                      {done ? 'Set complete' : `${pct}% complete`}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </PassportChrome>
  );
};

export default PassportCollectionsPage;
